import { useGSAP } from '@gsap/react';
import {
	Breakpoints,
	BreakpointWrapper,
	Container,
	ContainerGutter,
	ContainerPadding,
	DecorativeImage,
	DesignTokenSizeEnum,
	Gallery,
	GallerySlide,
	Layout,
	LayoutProps,
	ReferenceBadge,
	Text,
	TokenTextAppearance,
	TokenTextColor,
} from '@volkswagen-onehub/components-core';
import gsap from 'gsap';
import * as React from 'react';
import styled from 'styled-components';
import { GalleryNav } from '../components/gallery-nav';
import { ResponsiveText } from '../components/responsive-text';
import { MastersContext } from '../context/masters-context';
import { useDisclaimer } from '../use-disclaimer';

const gridDisplay = {
	[Breakpoints.default]: [
		{ name: '.', columns: 2 },
		{ name: 'info-title', columns: 20 },
		{ name: '.', columns: 2 },
		{ name: '.', columns: 2 },
		{ name: 'info-content', columns: 20 },
		{ name: '.', columns: 2 },
		{ name: '.', columns: 0 },
		{ name: 'info-badges', columns: 24 },
		{ name: '.', columns: 0 },
	],
};

const InfoSectionLayout = styled(Layout)<LayoutProps>`
	padding: var(--size-dynamic0200) 0;
	padding-bottom: 0;
	background-color: var(--c-white);

	& > div:nth-child(3) {
		padding: 0 var(--size-grid002);
	}
`;

const GallerySlideContainer = styled(GallerySlide)`
	height: 100%;
	& > div {
		width: 100%;
		height: 100%;
	}
`;

const InfoContentRow = styled.div`
	display: flex;
	flex-direction: column;
	align-items: stretch;
	height: 100%;
	gap: 0;

	@media (min-width: 960px) {
		flex-direction: row;
		gap: var(--size-grid001);
	}
`;
const InfoContentItem = styled.div`
	width: 100%;
	height: 100%;
	padding: var(--size-dynamic0100) 0;
	& :first-child {
		margin-bottom: var(--size-dynamic0020);
	}
	border-bottom: 1px solid var(--c-grey200);
	@media (min-width: 560px) {
		width: var(--size-grid010);
	}
	@media (min-width: 960px) {
		width: var(--size-grid006);
	}
	@media (min-width: 1920px) {
		height: unset;
	}
`;

const DescriptionText = styled(Text)`
	text-wrap: pretty;
`;

const BadgesContainer = styled.div`
	margin-top: var(--size-dynamic0200);
`;

const Badges = styled.div`
	display: flex;
	justify-content: space-between;
	align-items: center;
	flex-wrap: nowrap;
	gap: var(--size-grid001);

	& img {
		width: 100%;
		max-width: 290px;
		max-height: 165px;
	}

	@media (min-width: 960px) {
		gap: var(--size-grid002);
	}
`;

const StyledReferenceBadge = styled(ReferenceBadge)`
	vertical-align: top;
	&::before {
		content: none;
	}
`;

export const MastersInfo = (): React.ReactElement => {
	const { disclaimerManager } = React.useContext(MastersContext);
	const priceDisclaimerRef = useDisclaimer(
		'Consulta las <a href="https://www.volkswagen.es/idhub/content/dam/onehub_pkw/importers/es/pdf/Bases-Legales-Promocio%CC%81n-MASTER-VOLKSWAGEN-2026.pdf" target="_blank">bases legales de la promoción</a> para más información.',
		'footer-reference',
		disclaimerManager,
	);

	const infoContent = [
		[
			{
				title: 'Escuelas',
				description: (
					<>
						<b>3 experiencias de conducción</b> para poner a prueba nuestros Volkswagen más icónicos.
					</>
				),
			},
			{
				title: 'Viaje incluido',
				description: (
					<>
						<b>Durante 3 fines de semana</b> con hotel, transporte y comidas incluidos.
					</>
				),
			},
			{
				title: 'Destinos',
				description: (
					<>
						<b>En 3 destinos distintos</b> para completar el recorrido: Madrid, Valencia y Andorra.
					</>
				),
			},
		],
		[
			{
				title: 'Acompañante',
				description: (
					<>
						<b>Una segunda plaza</b> para vivir toda la experiencia acompañado.
					</>
				),
			},
			{
				title: 'Kit de regalo',
				description: (
					<>
						Un <b>pack con merchandising de Volkswagen</b> para acompañarte en tu camino.
					</>
				),
			},
			{
				title: 'Precio',
				description: (
					<>
						Toda la experiencia está valorada en 3.500€{' '}
						{priceDisclaimerRef && <StyledReferenceBadge>{priceDisclaimerRef.replace(/\.$/, '')}</StyledReferenceBadge>}
						, <b>pero para los participantes no tiene coste.</b>
					</>
				),
			},
		],
	];

	const desktopBadgesRef = React.useRef<HTMLDivElement>(null);
	const mobileBadgesRef = React.useRef<HTMLDivElement>(null);

	useGSAP(() => {
		if (mobileBadgesRef.current) {
			const mob = mobileBadgesRef.current.children;
			const tlm = gsap.timeline({
				scrollTrigger: {
					trigger: mobileBadgesRef.current,
					start: 'top 70%',
					toggleActions: 'play none none none',
					once: true,
				},
			});

			tlm.fromTo(mob[0], { opacity: 0, x: -60 }, { opacity: 1, x: 0, duration: 0.6, ease: 'power1.out' });
			tlm.fromTo(mob[1], { opacity: 0, x: 60 }, { opacity: 1, x: 0, duration: 0.6, ease: 'power1.out' }, 0);
		}

		if (desktopBadgesRef.current) {
			const badges = desktopBadgesRef.current.children;
			const tl = gsap.timeline({
				scrollTrigger: {
					trigger: desktopBadgesRef.current,
					start: 'top 80%',
					toggleActions: 'play none none none',
					once: true,
				},
			});

			tl.fromTo(badges, { opacity: 0, x: -60 }, { opacity: 1, x: 0, stagger: 0.15, duration: 0.6, ease: 'power1.out' });
		}
	}, []);

	const renderInfoContentRows = () => {
		return infoContent.map((column, columnIndex) => (
			<GallerySlideContainer
				key={column[columnIndex].title}
				data-testid={`info-slide`}
				aria-labelledby={`slide${columnIndex}-heading`}
			>
				<InfoContentRow key={columnIndex}>
					{column.map((info, index) => (
						<InfoContentItem key={index}>
							<Text appearance={TokenTextAppearance.headline300} color={TokenTextColor.primary}>
								{info.title}
							</Text>
							<DescriptionText appearance={TokenTextAppearance.copy200} color={TokenTextColor.primary}>
								{info.description}
							</DescriptionText>
						</InfoContentItem>
					))}
				</InfoContentRow>
			</GallerySlideContainer>
		));
	};

	return (
		<InfoSectionLayout data-testid="masters-info-section" appearance={gridDisplay}>
			<Container padding={{ bottom: ContainerPadding.dynamic0100 }} data-testid="info-heading" wrap={'always'}>
				<ResponsiveText
					appearance={{
						[Breakpoints.default]: TokenTextAppearance.headline400,
						[Breakpoints.b960]: TokenTextAppearance.headline500,
					}}
					color={TokenTextColor.primary}
				>
					¿Qué incluye <b>el máster Volkswagen?</b>
				</ResponsiveText>
			</Container>
			<Container>
				<BreakpointWrapper max={Breakpoints.b560}>
					<Gallery
						id="masters-info-gallery"
						aria-label="Masters Info Gallery"
						gutter={DesignTokenSizeEnum.grid001}
						bottomElements={
							<div style={{ paddingTop: '30px' }}>
								<GalleryNav />
							</div>
						}
					>
						{renderInfoContentRows()}
					</Gallery>
				</BreakpointWrapper>
				<BreakpointWrapper min={Breakpoints.b560}>
					<Container
						data-testid="intro-content"
						wrap={{
							[Breakpoints.default]: 'never',
							[Breakpoints.b960]: 'always',
						}}
						gutter={{
							[Breakpoints.default]: ContainerGutter.grid001,
							[Breakpoints.b960]: ContainerGutter.static0,
						}}
					>
						{renderInfoContentRows()}
					</Container>
				</BreakpointWrapper>
			</Container>

			<BadgesContainer data-testid="badges-container">
				<BreakpointWrapper max={Breakpoints.b560}>
					<div ref={mobileBadgesRef} style={{ display: 'flex', flexDirection: 'column' }}>
						<DecorativeImage src="https://assets.volkswagen.com/is/image/volkswagenag/1badges_mobile_part1_1?Zml0PWNyb3AlMkMxJndpZD05ODcmaGVpPTQwMSZmbXQ9cG5nLWFscGhhJmJmYz1vZmYmNWI1Yg==" />
						<DecorativeImage src="https://assets.volkswagen.com/is/image/volkswagenag/1badges_mobile_part2_1_1?Zml0PWNyb3AlMkMxJndpZD0xMDA4JmhlaT00NjImZm10PXBuZy1hbHBoYSZiZmM9b2ZmJjc5YmQ=" />
					</div>
				</BreakpointWrapper>
				<BreakpointWrapper min={Breakpoints.b560}>
					<Badges ref={desktopBadgesRef}>
						<div>
							<img src="https://assets.volkswagen.com/is/image/volkswagenag/badge_master_de_la_carretera-v2?Zml0PWNyb3AlMkMxJndpZD01MTgmaGVpPTUyMSZmbXQ9cG5nLWFscGhhJmJmYz1vZmYmYjNkYQ==" />
						</div>
						<div>
							<img src="https://assets.volkswagen.com/is/image/volkswagenag/badge_master_gti-v2?Zml0PWNyb3AlMkMxJndpZD03MjYmaGVpPTM0NSZmbXQ9cG5nLWFscGhhJmJmYz1vZmYmZmQ0ZA==" />
						</div>
						<div>
							<img src="https://assets.volkswagen.com/is/image/volkswagenag/1badge_master_track_experience%201_1?Zml0PWNyb3AlMkMxJndpZD03MjMmaGVpPTUxOSZmbXQ9cG5nLWFscGhhJmJmYz1vZmYmNDJlNw==" />
						</div>
						<div>
							<img src="https://assets.volkswagen.com/is/image/volkswagenag/badge_master_hielo_nieve-v2?Zml0PWNyb3AlMkMxJndpZD01MzImaGVpPTUyMSZmbXQ9cG5nLWFscGhhJmJmYz1vZmYmNmNkYQ==" />
						</div>
					</Badges>
				</BreakpointWrapper>
			</BadgesContainer>
		</InfoSectionLayout>
	);
};
